import * as moment from 'moment';
import * as React from 'react';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { IWatrReimbursement, IWatrRequest, IWageAndTravelVerifyValidation, IPaystub } from '@nkr/models/index';
import { FormControl, Button, Panel, Table, Alert, Glyphicon } from 'react-bootstrap';
import { FileDocumentType } from '@nkr/enums/document-type';
import { WageAndTravelReimbursementRequestStatus } from '@nkr/enums/watr-reimbursement-request-status';
import { DonorShieldContext } from '../context';
import WatrInfoAccordions from '../accordion';
import Header from '../header';
import VerificationStepTracker from '../verification-step-tracker';
import { formatMoneyInput, parseMoneyInput } from '../common/money-input';
import { isHourly, isSelfEmployed } from '../utils/employment-type.utils';
import ConnectedDevMockDocsOffer from '../dev-mock-docs/ConnectedDevMockDocsOffer';
import { IWatrViewerDocument } from '../../utils/watr-viewer-document';

const paystubCheckboxStyle = (checked: boolean) => ({
    cursor: 'pointer' as const,
    width: '26px',
    height: '26px',
    border: '2px solid #ffc107',
    borderRadius: '5px',
    backgroundColor: checked ? '#ffc107' : 'transparent',
    appearance: 'none' as const,
    WebkitAppearance: 'none' as const,
    MozAppearance: 'none' as const,
    position: 'relative' as const,
    ...(checked && {
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 12 12'%3E%3Cpath fill='white' d='M10 3L4.5 8.5L2 6' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        backgroundSize: '14px 14px'
    })
});

const getPaystubRowStyle = (isViewing: boolean, reviewRequired: boolean, isApproved: boolean): React.CSSProperties => {
    const baseStyle: React.CSSProperties = {
        borderBottom: '1px solid #f0f0f0',
        borderLeft: '4px solid transparent',
        cursor: 'pointer',
        transition: 'background-color 0.2s ease, border-left-color 0.2s ease, box-shadow 0.2s ease'
    };

    if (reviewRequired) {
        baseStyle.borderLeft = isViewing ? '4px solid #0d6efd' : '4px solid #ffc107';
        baseStyle.backgroundColor = '#fff3cd';

        if (isViewing) {
            baseStyle.boxShadow = 'inset 4px 0 0 #0d6efd, 0 1px 4px rgba(13, 110, 253, 0.2)';
        }

        return baseStyle;
    }

    if (isApproved) {
        baseStyle.borderLeft = isViewing ? '4px solid #0d6efd' : '4px solid #28a745';
        baseStyle.backgroundColor = '#d4edda';

        if (isViewing) {
            baseStyle.boxShadow = 'inset 4px 0 0 #0d6efd, 0 1px 4px rgba(40, 167, 69, 0.2)';
        }

        return baseStyle;
    }

    if (isViewing) {
        baseStyle.borderLeft = '4px solid #0d6efd';
        baseStyle.backgroundColor = '#d1ecf1';
        baseStyle.boxShadow = 'inset 4px 0 0 #0d6efd, 0 1px 4px rgba(13, 110, 253, 0.2)';
    }

    return baseStyle;
};

interface IProps {
    data: IWatrRequest,
    isAudit: boolean,
    reimbursements: IWatrReimbursement[],
    validation: IWageAndTravelVerifyValidation,
    isDirty: boolean
}

const VerifyStub = (props: IProps) => {
    const [paystubs, setPaystubs] = useState<IPaystub[]>([]);
    const [draftOtherAmounts, setDraftOtherAmounts] = useState<Record<number, number>>({});
    const [grossFocusIndex, setGrossFocusIndex] = useState<number | null>(null);
    const [viewingIndex, setViewingIndex] = useState(0);
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [approvedPaystubIds, setApprovedPaystubIds] = useState<Set<number>>(new Set());
    const fileLoadAttemptedForRequestId = useRef<number | null>(null);

    const context = useContext(DonorShieldContext);

    const employmentType = props.data.employmentType;
    const isHourlyValue = isHourly(employmentType);
    const isSelfEmployedValue = isSelfEmployed(employmentType);

    const hasHeaderPayStubSummary =
        (props.data.incomePayStubGross != null && Number(props.data.incomePayStubGross) > 0) ||
        (props.data.incomePayStubStartDate != null && String(props.data.incomePayStubStartDate).length > 0) ||
        (props.data.incomePayStubEndDate != null && String(props.data.incomePayStubEndDate).length > 0);

    const filteredWeeks = props.data.requestWeeks?.filter(w => w.status > WageAndTravelReimbursementRequestStatus.PendingApproval && w.status < WageAndTravelReimbursementRequestStatus.CenterDeclined) || [];

    const persistDraftOtherAmounts = async () => {
        if (!context.onUpdateRequestWeekOtherAmount) return;
        const updates = filteredWeeks.filter(w => {
            if ((w.reimbursementId || 0) > 0) return false;
            const newAmount = draftOtherAmounts[w.weekRequested] ?? w.otherAmount ?? 0;
            const currentAmount = w.otherAmount ?? 0;
            return newAmount !== currentAmount;
        });
        await Promise.all(updates.map(w =>
            context.onUpdateRequestWeekOtherAmount(props.data.id, w.weekRequested, draftOtherAmounts[w.weekRequested] ?? w.otherAmount ?? 0)
        ));
    };

    const sourcePaystubs = props.data.paystubs && Array.isArray(props.data.paystubs)
        ? props.data.paystubs
        : [];

    const sortedPaystubs = useMemo(() => {
        return [...sourcePaystubs].sort((a, b) => {
            const seq = (a.paystubSequence ?? 0) - (b.paystubSequence ?? 0);

            if (seq !== 0) {
                return seq;
            }

            const start = moment(a.startDate).valueOf() - moment(b.startDate).valueOf();

            if (start !== 0) {
                return start;
            }

            return (a.id || 0) - (b.id || 0);
        });
    }, [sourcePaystubs]);

    const viewerDocs = useMemo<IWatrViewerDocument[]>(() => {
        if (sortedPaystubs.length === 0) {
            if (isSelfEmployedValue) {
                return [];
            }

            return [{
                docType: FileDocumentType.PayStub,
                lookupId: props.data.id,
                label: 'Pay Stub'
            }];
        }

        return sortedPaystubs.map((paystub, index) => ({
            docType: FileDocumentType.PayStub,
            lookupId: paystub.paystubFileId || props.data.id,
            label: `Pay Stub ${paystub.paystubSequence || index + 1}`
        }));
    }, [sortedPaystubs, isSelfEmployedValue, props.data.id]);

    const paystubFileIds = useMemo(
        () => (paystubs.length > 0 ? paystubs : sortedPaystubs)
            .map(paystub => paystub.id)
            .filter((id): id is number => id != null),
        [paystubs, sortedPaystubs]
    );

    const paystubFileMap = useMemo(() => {
        const map = new Map<number, any[]>();
        const rows = paystubs.length > 0 ? paystubs : sortedPaystubs;

        rows.forEach((paystub) => {
            if (paystub.id == null) {
                return;
            }

            map.set(paystub.id, [{
                id: paystub.id,
                amount: paystub.grossAmount,
                expenseDate: paystub.startDate,
                endDate: paystub.endDate,
                reviewRequired: paystub.reviewRequired === true,
                approved: approvedPaystubIds.has(paystub.id),
                expenseType: 0
            }]);
        });

        return map;
    }, [paystubs, sortedPaystubs, approvedPaystubIds]);

    const markPaystubsApproved = useCallback((paystubIds: number[], approved: boolean) => {
        setApprovedPaystubIds(prev => {
            const next = new Set(prev);

            paystubIds.forEach(id => {
                if (approved) {
                    next.add(id);
                } else {
                    next.delete(id);
                }
            });

            return next;
        });
    }, []);

    const applyReviewRequired = useCallback(async (paystubIds: number[], reviewRequired: boolean) => {
        setPaystubs(prev => prev.map(item =>
            paystubIds.includes(item.id) ? { ...item, reviewRequired } : item
        ));
        markPaystubsApproved(paystubIds, !reviewRequired);

        if (!context.onSetPaystubReviewRequired) {
            return;
        }

        await Promise.all(paystubIds.map(id => context.onSetPaystubReviewRequired!(id, reviewRequired)));
    }, [context, markPaystubsApproved]);

    const openViewerAtIndex = useCallback(async (index: number) => {
        if (!viewerDocs.length || index < 0 || index >= viewerDocs.length) {
            return;
        }

        const doc = viewerDocs[index];

        try {
            const fileUrl = await context.fetchFileUrl(doc.docType, doc.lookupId);

            if (!fileUrl) {
                return;
            }

            setViewingIndex(index);
            context.onShowDataSideBar(
                doc.docType,
                doc.lookupId,
                fileUrl,
                paystubFileIds.length,
                paystubFileIds,
                index,
                paystubFileMap,
                async (paystubIds) => {
                    await applyReviewRequired(paystubIds, false);
                },
                async (paystubIds) => {
                    await applyReviewRequired(paystubIds, true);
                },
                undefined,
                undefined,
                undefined,
                (paystubIds, reviewRequired) => {
                    setPaystubs(prev => prev.map(item =>
                        paystubIds.includes(item.id) ? { ...item, reviewRequired } : item
                    ));
                    markPaystubsApproved(paystubIds, !reviewRequired);
                },
                viewerDocs,
                (_docType, nextIndex) => setViewingIndex(nextIndex)
            );
        } catch {
        }
    }, [viewerDocs, context, paystubFileIds, paystubFileMap, applyReviewRequired, markPaystubsApproved]);

    useEffect(() => {
        if (props.data.paystubs && Array.isArray(props.data.paystubs) && props.data.paystubs.length > 0) {
            setPaystubs([...props.data.paystubs]);
        } else {
            setPaystubs([]);
        }

        setEditingIndex(null);
    }, [props.data]);

    useEffect(() => {
        setApprovedPaystubIds(new Set());
        setViewingIndex(0);
    }, [props.data.id]);

    useEffect(() => {
        const requestId = props.data.id;

        if (fileLoadAttemptedForRequestId.current === requestId) {
            return;
        }

        if (!viewerDocs.length) {
            return;
        }

        fileLoadAttemptedForRequestId.current = requestId;
        openViewerAtIndex(0);
    }, [props.data.id, viewerDocs, openViewerAtIndex]);

    const handleFileDownload = async (docType: number) => {
        try {
            const response = await context.fetchFileUrl(docType, props.data.id);
            context.onShowDataSideBar(docType, props.data.id, response);
        } catch {
        }
    };

    const isPaystubViewing = (paystub: IPaystub) => {
        const viewed = sortedPaystubs[viewingIndex];

        return viewed != null && paystub.id != null && viewed.id === paystub.id;
    };

    const handleReviewRequiredChange = async (paystub: IPaystub, checked: boolean) => {
        if (!paystub.id) {
            return;
        }

        setPaystubs(prev => prev.map(item =>
            item.id === paystub.id ? { ...item, reviewRequired: checked } : item
        ));

        if (checked) {
            markPaystubsApproved([paystub.id], false);
        }

        if (context.onSetPaystubReviewRequired) {
            try {
                await context.onSetPaystubReviewRequired(paystub.id, checked);
            } catch {
            }
        }
    };

    const handleViewPaystubRow = async (paystub: IPaystub) => {
        const index = sortedPaystubs.findIndex(item => item.id === paystub.id);

        if (index >= 0) {
            await openViewerAtIndex(index);
        }
    };

    const handleModifyClick = (index: number, isEditing: boolean) => {
        setEditingIndex(isEditing ? null : index);
    };

    const handleRowClick = (event: React.MouseEvent, paystub: IPaystub) => {
        const target = event.target as HTMLElement;

        if (target.closest('input, button, a, select, textarea, label')) {
            return;
        }

        handleViewPaystubRow(paystub);
    };

    const handlePaystubChange = (index: number, field: 'startDate' | 'endDate' | 'grossAmount', value: string | number) => {
        const updatedPaystubs = [...paystubs];
        if (field === 'grossAmount') {
            const amount = typeof value === 'number' ? value : parseMoneyInput(String(value));
            updatedPaystubs[index] = { ...updatedPaystubs[index], grossAmount: amount };
        } else {
            updatedPaystubs[index] = { ...updatedPaystubs[index], [field]: value };
        }
        setPaystubs(updatedPaystubs);
    };

    const formatCoverageRange = (startText: string, endText: string) => {
        if (!startText || startText === '-' || !endText || endText === '-') {
            return 'Dates not available';
        }

        const start = moment(startText, 'MM/DD/YYYY');
        const end = moment(endText, 'MM/DD/YYYY');

        if (!start.isValid() || !end.isValid()) {
            return `${startText} – ${endText}`;
        }

        if (start.year() === end.year()) {
            return `${start.format('MMM D')} – ${end.format('MMM D, YYYY')}`;
        }

        return `${start.format('MMM D, YYYY')} – ${end.format('MMM D, YYYY')}`;
    };

    const calculateOverview = () => {
        const flaggedCount = paystubs.filter(paystub => paystub.reviewRequired === true).length;

        if (paystubs.length === 0) {
            const earliestDate = props.data.incomePayStubStartDate ? moment(props.data.incomePayStubStartDate).format('MM/DD/YYYY') : '-';
            const latestDate = props.data.incomePayStubEndDate ? moment(props.data.incomePayStubEndDate).format('MM/DD/YYYY') : '-';
            const totalGross = props.data.incomePayStubGross || 0;

            return {
                earliestDate,
                latestDate,
                coverage: formatCoverageRange(earliestDate, latestDate),
                totalGross,
                stubCount: 0,
                flaggedCount: 0,
                missingDates: earliestDate === '-' || latestDate === '-',
                missingGross: !(totalGross > 0)
            };
        }

        const earliestDate = paystubs.reduce((earliest, paystub) => {
            const paystubDate = moment(paystub.startDate);
            return !earliest || paystubDate.isBefore(earliest) ? paystubDate : earliest;
        }, null as moment.Moment | null);

        const latestDate = paystubs.reduce((latest, paystub) => {
            const paystubDate = moment(paystub.endDate);
            return !latest || paystubDate.isAfter(latest) ? paystubDate : latest;
        }, null as moment.Moment | null);

        const totalGross = paystubs.reduce((sum, paystub) => sum + paystub.grossAmount, 0);
        const startText = earliestDate ? earliestDate.format('MM/DD/YYYY') : '-';
        const endText = latestDate ? latestDate.format('MM/DD/YYYY') : '-';

        return {
            earliestDate: startText,
            latestDate: endText,
            coverage: formatCoverageRange(startText, endText),
            totalGross,
            stubCount: paystubs.length,
            flaggedCount,
            missingDates: startText === '-' || endText === '-',
            missingGross: !(totalGross > 0)
        };
    };

    const getVerifyStubDates = () => {
        if (paystubs.length > 0) {
            const earliest = paystubs.reduce((earliest, p) => moment(p.startDate).isBefore(moment(earliest)) ? p.startDate : earliest, paystubs[0].startDate);
            const latest = paystubs.reduce((latest, p) => moment(p.endDate).isAfter(moment(latest)) ? p.endDate : latest, paystubs[0].endDate);
            return {
                earliestDate: moment(earliest).format('YYYY-MM-DD'),
                latestDate: moment(latest).format('YYYY-MM-DD')
            };
        }
        const startM = moment(props.data.incomePayStubStartDate);
        const endM = moment(props.data.incomePayStubEndDate);
        return {
            earliestDate: startM.isValid() ? startM.format('YYYY-MM-DD') : '',
            latestDate: endM.isValid() ? endM.format('YYYY-MM-DD') : ''
        };
    };

    const verifyStub = async () => {
        await persistDraftOtherAmounts();
        const overview = calculateOverview();
        const { earliestDate, latestDate } = getVerifyStubDates();
        const totalGross = overview.totalGross;
        
        context.onVerifyPayStub(props.data.id, earliestDate, latestDate, totalGross, paystubs, false, props.data.ssnDecrypted);
    };

    const verifyStubWithEmail = async () => {
        await persistDraftOtherAmounts();
        const overview = calculateOverview();
        const { earliestDate, latestDate } = getVerifyStubDates();
        const totalGross = overview.totalGross;
        
        context.onVerifyPayStub(props.data.id, earliestDate, latestDate, totalGross, paystubs, true, props.data.ssnDecrypted);
    };

    const declineStub = async () => {
        if (paystubs.every(paystub => paystub.reviewRequired !== true)) {
            return;
        }

        await persistDraftOtherAmounts();
        context.onShowDeclineModal(props.data.id, false);
    };

    const overview = calculateOverview();
    const reviewLabel = overview.stubCount === 0
        ? '—'
        : String(overview.flaggedCount);
    const reviewDetail = overview.stubCount === 0
        ? 'Waiting on paystub lines'
        : (overview.flaggedCount > 0
            ? `${overview.flaggedCount} of ${overview.stubCount} need review`
            : 'None marked yet');

    return (
        <div>
            <ConnectedDevMockDocsOffer
                data={props.data}
                downloadFile={(docType, lookupId) => context ? context.downloadFile(docType, lookupId as number) : Promise.resolve()}
                includeWages={true}
                includeReceipts={false}
                forceWageSlots={{ forcePaystub: true }}
                onComplete={() => {
                    fileLoadAttemptedForRequestId.current = null;
                    if (context.onRefreshRequest) {
                        context.onRefreshRequest();
                    }
                }}
            />
            <Header data={props.data} showStatus={false} />
            <VerificationStepTracker data={props.data} />

            <Panel>
                <Panel.Heading>
                    <Panel.Title componentClass="h3">Overview</Panel.Title>
                </Panel.Heading>
                <Panel.Body style={{ padding: '15px' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                        <div style={{
                            flex: '1 1 180px',
                            minWidth: 160,
                            padding: '14px 16px',
                            borderRadius: 8,
                            backgroundColor: overview.missingDates ? '#fff3cd' : '#f8fafc',
                            border: overview.missingDates ? '1px solid #ffc107' : '1px solid #e5e7eb'
                        }}>
                            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#6b7280', marginBottom: 6 }}>
                                Coverage
                            </div>
                            <div style={{ fontSize: 18, fontWeight: 650, color: overview.missingDates ? '#856404' : '#111827', lineHeight: 1.25 }}>
                                {overview.coverage}
                            </div>
                            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                                {overview.stubCount > 0
                                    ? `${overview.stubCount} paystub${overview.stubCount === 1 ? '' : 's'}`
                                    : 'No paystub lines'}
                            </div>
                        </div>
                        {(() => {
                            const weekly = Number(props.data.calculatedWeeklyPayByPayStub) || 0;
                            const showWeeklyHero = isHourlyValue && overview.stubCount > 1 && weekly > 0;
                            const heroAmount = showWeeklyHero ? weekly : overview.totalGross;
                            const heroMissing = showWeeklyHero ? !(weekly > 0) : overview.missingGross;

                            return (
                                <div style={{
                                    flex: '1 1 180px',
                                    minWidth: 160,
                                    padding: '14px 16px',
                                    borderRadius: 8,
                                    backgroundColor: heroMissing ? '#fff3cd' : '#f8fafc',
                                    border: heroMissing ? '1px solid #ffc107' : '1px solid #e5e7eb'
                                }}>
                                    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#6b7280', marginBottom: 6 }}>
                                        {showWeeklyHero ? 'Paystub weekly' : 'Combined gross'}
                                    </div>
                                    <div style={{ fontSize: 18, fontWeight: 650, color: heroMissing ? '#856404' : '#111827', lineHeight: 1.25 }}>
                                        ${formatMoneyInput(heroAmount)}
                                    </div>
                                    <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                                        {heroMissing
                                            ? (showWeeklyHero ? 'Weekly is missing or $0' : 'Gross is missing or $0')
                                            : (showWeeklyHero
                                                ? `Combined $${formatMoneyInput(overview.totalGross)} across ${overview.stubCount} stubs`
                                                : 'Sum of stub wages')}
                                    </div>
                                </div>
                            );
                        })()}
                        <div style={{
                            flex: '1 1 180px',
                            minWidth: 160,
                            padding: '14px 16px',
                            borderRadius: 8,
                            backgroundColor: overview.flaggedCount > 0 ? '#fff3cd' : '#f8fafc',
                            border: overview.flaggedCount > 0 ? '1px solid #ffc107' : '1px solid #e5e7eb'
                        }}>
                            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#6b7280', marginBottom: 6 }}>
                                Flagged for review
                            </div>
                            <div style={{ fontSize: 18, fontWeight: 650, color: overview.flaggedCount > 0 ? '#856404' : '#111827', lineHeight: 1.25 }}>
                                {reviewLabel}
                            </div>
                            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                                {reviewDetail}
                            </div>
                        </div>
                    </div>
                </Panel.Body>
            </Panel>

            <Panel style={{ marginTop: 15 }}>
                <Panel.Heading>
                    <Panel.Title componentClass="h3">Verification Required</Panel.Title>
                </Panel.Heading>
                <Panel.Body style={{ padding: '15px' }}>
                    {!isSelfEmployedValue && paystubs.length === 0 && hasHeaderPayStubSummary && (
                        <Alert bsStyle='warning' style={{ marginBottom: 15 }}>
                            No pay stub line items were returned for this request, but summary fields are present on the
                            request record. Overview below may reflect header-only data until wage reimbursement data is
                            consistent.
                        </Alert>
                    )}
                    {paystubs.length > 0 && (
                        <div>
                            <h4 style={{ marginTop: 0, marginBottom: 10, fontSize: '15px', fontWeight: 600, color: '#333' }}>Paystubs</h4>
                            <Table bordered style={{ marginBottom: 0, tableLayout: 'fixed' }}>
                                <thead>
                                    <tr>
                                        <th style={{ width: '104px', textAlign: 'center', lineHeight: 1.2 }} title='Mark the paystub if dates or amounts need finance review'>
                                            Flag for Review
                                        </th>
                                        <th style={{ width: '44px', textAlign: 'center' }}>#</th>
                                        <th style={{ width: '20%' }}>Start Date</th>
                                        <th style={{ width: '20%' }}>End Date</th>
                                        <th style={{ width: '20%', textAlign: 'right' }}>Gross Amount</th>
                                        <th style={{ width: '88px', textAlign: 'center' }}>Modify</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paystubs.map((paystub: IPaystub, index: number) => {
                                        const isEditing = editingIndex === index;

                                        return (
                                        <tr
                                            key={paystub.id || index}
                                            onClick={(event) => handleRowClick(event, paystub)}
                                            style={getPaystubRowStyle(
                                                isPaystubViewing(paystub),
                                                paystub.reviewRequired === true,
                                                paystub.id != null && approvedPaystubIds.has(paystub.id)
                                            )}>
                                            <td style={{ textAlign: 'center', verticalAlign: 'middle', width: '104px' }}>
                                                <input
                                                    type='checkbox'
                                                    title='Flag this paystub for finance review'
                                                    checked={paystub.reviewRequired === true}
                                                    disabled={!paystub.id}
                                                    onChange={(e) => handleReviewRequiredChange(paystub, e.currentTarget.checked)}
                                                    style={paystubCheckboxStyle(paystub.reviewRequired === true)} />
                                            </td>
                                            <td style={{ textAlign: 'center', verticalAlign: 'middle', width: '44px' }}>{paystub.paystubSequence || index + 1}</td>
                                            <td>
                                                {isEditing ? (
                                                    <FormControl
                                                        type='date'
                                                        value={moment(paystub.startDate).format('YYYY-MM-DD')}
                                                        onChange={(e: React.FormEvent<HTMLInputElement & FormControl>) => handlePaystubChange(index, 'startDate', e.currentTarget.value)}
                                                        style={{ width: '100%' }} />
                                                ) : (
                                                    <span>{moment(paystub.startDate).format('MM/DD/YYYY')}</span>
                                                )}
                                            </td>
                                            <td>
                                                {isEditing ? (
                                                    <FormControl
                                                        type='date'
                                                        value={moment(paystub.endDate).format('YYYY-MM-DD')}
                                                        onChange={(e: React.FormEvent<HTMLInputElement & FormControl>) => handlePaystubChange(index, 'endDate', e.currentTarget.value)}
                                                        style={{ width: '100%' }} />
                                                ) : (
                                                    <span>{moment(paystub.endDate).format('MM/DD/YYYY')}</span>
                                                )}
                                            </td>
                                            <td style={{ textAlign: 'right' }}>
                                                {isEditing ? (
                                                    <div className='input-group'>
                                                        <span className='input-group-addon'>$</span>
                                                        <FormControl
                                                            type='text'
                                                            inputMode='decimal'
                                                            value={grossFocusIndex === index
                                                                ? (paystub.grossAmount ? String(paystub.grossAmount) : '')
                                                                : formatMoneyInput(paystub.grossAmount || 0)}
                                                            onFocus={() => setGrossFocusIndex(index)}
                                                            onBlur={() => setGrossFocusIndex(null)}
                                                            onChange={(e: React.FormEvent<HTMLInputElement & FormControl>) => handlePaystubChange(index, 'grossAmount', e.currentTarget.value)}
                                                            style={{ textAlign: 'right' }} />
                                                    </div>
                                                ) : (
                                                    <span>${formatMoneyInput(paystub.grossAmount || 0)}</span>
                                                )}
                                            </td>
                                            <td style={{ textAlign: 'center', verticalAlign: 'middle', width: '88px' }}>
                                                <Button
                                                    title={isEditing ? 'Done' : 'Modify'}
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        handleModifyClick(index, isEditing);
                                                    }}
                                                    style={{
                                                        minWidth: '40px',
                                                        minHeight: '36px',
                                                        padding: '6px 10px'
                                                    }}>
                                                    <Glyphicon
                                                        glyph={isEditing ? 'ok' : 'pencil'}
                                                        style={{ fontSize: '16px' }} />
                                                </Button>
                                            </td>
                                        </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </div>
                    )}

                    {!isHourlyValue && paystubs.length === 0 && (
                        <div style={{ padding: '15px 0', color: '#666', textAlign: 'center' }}>
                            No paystubs to verify
                        </div>
                    )}

                    {props.data.selfEmployedAffirmationFile && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 15 }}>
                            <Button onClick={() => handleFileDownload(FileDocumentType.DsSelfEmployedAffidavit)}>
                                View Self Employed Affidavit
                            </Button>
                        </div>
                    )}
                </Panel.Body>
            </Panel>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 15, paddingBottom: 15 }}>
                <Button
                    bsStyle='primary btn-danger'
                    onClick={declineStub}
                    disabled={overview.flaggedCount === 0}
                    title={overview.flaggedCount === 0 ? 'Flag at least one paystub for review before declining' : 'Decline'}
                    style={{ width: 110 }}>
                    Decline
                </Button>
                <Button
                    bsStyle='success'
                    onClick={verifyStub}
                    disabled={overview.flaggedCount > 0}
                    title={overview.flaggedCount > 0 ? 'Remove paystub review flags before verifying' : undefined}
                    style={{ width: 110 }}>
                    Verify
                </Button>
            </div>

            <WatrInfoAccordions
                data={props.data}
                isAudit={props.isAudit}
                reimbursements={props.reimbursements}
                allowEditWeekOtherAmount={true}
                draftWeekOtherAmounts={draftOtherAmounts}
                onWeekOtherAmountChange={(weekRequested, otherAmount) => setDraftOtherAmounts(prev => ({ ...prev, [weekRequested]: otherAmount }))}
            />
        </div>
    );
}

export default VerifyStub;